// Parchment fog on the land: where no source has touched a hex for a long time the map turns to old
// paper (rendering.md 7.1). One surface layer, drawn after the political layers so borders sit
// under it and show through faintly:
//
//   paper ─┬─ terrain tone ........... the colour underneath sets how dark the paper is there
//          ├─ ink contour lines ...... from the real elevation (index lines every 4th)
//          ├─ ink coast line ......... where the land meets the sea
//          ├─ hachure ................ diagonal strokes where the ground is rough (relief)
//          └─ burn rim ............... 1 s glow where fresh data lifts the fog
//
// The fog is decoration for a fact: it says only "no recent source" (staleness), never a value.
// Its edge is a noisy threshold on the staleness field, so it never follows the hex grid.
//
// Two TSL rules shape the code (see lens.ts): inputs are pinned to variables before the branch,
// and the branch fills separate result variables.

import { If, float, fwidth, mix, smoothstep, vec2, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { TERRAIN } from '../../core/schema.ts';
import type { SurfaceCtx, SurfaceLayer } from '../surface.ts';
import { rgb } from '../tsl/color.ts';
import { fbmW } from './wrapNoise.ts';
import type { FogState } from './fogState.ts';
import { BURN_EMBER, BURN_INK, FOG_ACTIVE, INK, fieldAt, fogLook, paperAt } from './parchment.ts';
import { luma, stripes } from './patterns.ts';
import type { GroundTerms } from './strategic.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** After the political layers (borders 300, claims 400-499), before highlight (900). */
export const FOG_ORDER = 850;

const BYTE = 255;
/** Land keeps a trace of its colour even in full fog; the sea veil goes further (seaVeil.ts). */
const LAND_MAX = 0.92;
/** Under a lens the fog steps back this share, so the lens colours stay readable. */
const LENS_FOG_CUT = 0.8;
/** Share of what the political layers drew (borders, claims, rivers) that survives on the paper. */
const KEEP_POLITICAL = 0.6;
const EMISSIVE_LIFT = 0.22;
const PAPER_ROUGHNESS = 0.95;

// Terrain tone: the paper is darker over forest and lighter over desert, so the map still reads
// as land under the fog. LUM_REF is the linear luma of an average green hex.
const TONE_LUM_REF = 0.12;
const TONE_RANGE = [0.55, 1.2] as const;
const TONE_SHARE = 0.55;

// Contours every CONTOUR_M metres of real elevation, every INDEX_EVERY-th line heavier. The
// elevation is wobbled a little so the piecewise-linear field never shows its hex facets.
const CONTOUR_M = 250;
const INDEX_EVERY = 4;
const CONTOUR_PX = 1.1;
const CONTOUR_INK = 0.6;
const INDEX_INK = 0.9;
const CONTOUR_SPACING_PX = [3, 8] as const;
const WOBBLE_M = 90;
const WOBBLE_FREQ = 1.3;

// Coast line: just above the sea veil so it is never buried under it.
const COAST_Y = 0.022;
const COAST_PX = 1.1;
const COAST_INK = 0.85;

// Hachure where the ground is rough (highest point of the hex above its mean, in metres).
const HATCH_FREQ = 7.5;
const HATCH_DUTY = 0.24;
const HATCH_ROUGH_M = [150, 900] as const;
const HATCH_WOBBLE = 0.12;
const HATCH_WOBBLE_FREQ = 1.6;
const HATCH_INK = 0.7;
const EPS = 1e-6;

export interface FogLayer {
  layer: SurfaceLayer;
}

export function createFog(state: FogState, lensAmount: F, ground: GroundTerms): FogLayer {
  let gate: F | null = null;
  let glow: V3 | null = null;

  /** Ink coverage 0..1 of the contour lines: distance in pixels to the nearest level, fading when dense. */
  function contours(metres: F, dm: F): F {
    const t = metres.div(CONTOUR_M);
    const nearest = t.add(0.5).floor();
    const dist = t.sub(nearest).abs().mul(CONTOUR_M).div(dm.max(EPS));
    const line = smoothstep(CONTOUR_PX + 1, CONTOUR_PX, dist);
    const major = float(1).sub(nearest.mod(INDEX_EVERY).min(1));
    const spacing = float(CONTOUR_M).div(dm.max(EPS));
    const ashore = nearest.abs().min(1);
    return line.mul(ashore).mul(mix(float(CONTOUR_INK), float(INDEX_INK), major)).mul(smoothstep(CONTOUR_SPACING_PX[0], CONTOUR_SPACING_PX[1], spacing));
  }

  function paint(ctx: SurfaceCtx): V3 {
    const painted = vec3(0).toVar('fogPainted');
    const opacity = float(0).toVar('fogOpacity');
    const ember = vec3(0).toVar('fogEmber');
    const under = ctx.color.toVar('fogUnder');
    const base = ctx.base.toVar('fogBase');

    // Derivatives and the paper sample stay outside the branch (WGSL uniformity).
    const h = ctx.world.y.toVar('fogH');
    const dh = fwidth(h).toVar('fogDh');
    const relief = ctx.data.reliefSmooth(ctx.hex).toVar('fogRelief');
    const wobble = fbmW(ctx.xz, WOBBLE_FREQ).sub(0.5).mul(WOBBLE_M);
    const metres = relief.x.add(wobble).toVar('fogMetres');
    const dm = fwidth(metres).toVar('fogDm');
    const field = fieldAt(state.data, ctx.hex).toVar('fogField');
    const sheet = paperAt(ctx.xz, ctx.px, field.x.div(BYTE)).toVar('fogPaper');
    const land = ctx.data.terrainAt(ctx.hex).terrain.greaterThan(TERRAIN.COAST + 0.5);
    const lensKeep = float(1).sub(lensAmount.mul(LENS_FOG_CUT));
    const active = land.and(field.x.max(field.y).greaterThan(FOG_ACTIVE));

    If(active, () => {
      const look = fogLook(field, ctx.xz, state.reveal);
      const cover = look.cover.mul(LAND_MAX).mul(lensKeep);

      // Paper tone from the terrain underneath.
      const tone = luma(base).div(TONE_LUM_REF).clamp(TONE_RANGE[0], TONE_RANGE[1]);
      const paper = sheet.mul(mix(float(1), tone, TONE_SHARE));

      // Ink.
      const coast = smoothstep(COAST_PX + 1, COAST_PX, h.sub(COAST_Y).abs().div(dh.max(EPS))).mul(COAST_INK);
      const rough = smoothstep(HATCH_ROUGH_M[0], HATCH_ROUGH_M[1], relief.y.sub(relief.x));
      const shake = fbmW(ctx.xz, HATCH_WOBBLE_FREQ).sub(0.5).mul(HATCH_WOBBLE);
      const hatch = stripes(ctx.xz.add(vec2(shake, shake)), HATCH_FREQ, HATCH_DUTY, ctx.px).mul(rough).mul(HATCH_INK);
      const ink = contours(metres, dm).max(coast).max(hatch);

      // What the layers above the ground drew (rivers, lens, borders) survives faintly.
      const political = under.sub(ground.flatBase(base));
      const inked = mix(paper.add(political.mul(KEEP_POLITICAL)).max(0), rgb(INK), ink.clamp(0, 1));
      const burnt = mix(inked, rgb(BURN_INK), look.rim.mul(0.85));

      painted.assign(burnt);
      opacity.assign(cover);
      ember.assign(rgb(BURN_EMBER).mul(look.rim).mul(0.6));
    });

    gate = opacity;
    // The paper covers the ground, so it also covers the ground's own emissive share.
    glow = ember.add(painted.mul(opacity).mul(EMISSIVE_LIFT)).sub(ground.lift().mul(opacity));
    return mix(under, painted, opacity);
  }

  return {
    layer: {
      name: 'fog',
      order: FOG_ORDER,
      color: paint,
      roughness: (ctx) => (gate ? mix(ctx.roughness, float(PAPER_ROUGHNESS), gate) : ctx.roughness),
      emissive: () => glow ?? vec3(0),
    },
  };
}
